import React, { ReactNode, createContext, useContext, useState } from 'react'
import { match } from 'ts-pattern'

import { ClassificationResult, DocumentTypeOption, OtherDocumentType } from '@documents/types'
import { DocumentIncomeRequirementFileType, DocumentIncomeRequirements } from '@frame/lib/api/tasks/types'
import { useStore } from '@frame/lib/stores/useStore'

import { SUPPORTED_CLASSIFICATION_TYPES } from '../../constants'
import { shouldHideW2s } from '../../v2/utils/documentRequirements'

type DocumentsFlowContextValue = {
  selectedDocumentType: DocumentTypeOption | null
  setSelectedDocumentType: (type: DocumentTypeOption | null) => void
  selectedOtherDocumentTypes: OtherDocumentType[]
  setSelectedOtherDocumentTypes: (types: OtherDocumentType[]) => void
  updateSelectedDocumentType: (value: DocumentTypeOption | null) => void
  updateSelectedOtherDocumentTypes: (value: OtherDocumentType) => void
  clearOtherDocumentTypeSelections: () => void
  documentIncomeRequirements: DocumentIncomeRequirements | null
  hideW2s: boolean
  supportedClassificationTypes: ClassificationResult[]
}

const DocumentsFlowContext = createContext<DocumentsFlowContextValue | null>(null)

type Props = {
  children: ReactNode
}

const documentIncomeTypeRequirementFileTypeToClassificationResult = (
  documentIncomeTypeRequirementFileType: DocumentIncomeRequirementFileType
): ClassificationResult => {
  return match(documentIncomeTypeRequirementFileType)
    .with(DocumentIncomeRequirementFileType.PAYSTUB, () => ClassificationResult.PAYSTUB)
    .with(DocumentIncomeRequirementFileType.W2, () => ClassificationResult.W2)
    .exhaustive()
}

function getSupportedDocumentClassificationResults(documentIncomeRequirements: DocumentIncomeRequirements | null) {
  if (!documentIncomeRequirements) {
    return SUPPORTED_CLASSIFICATION_TYPES
  }

  const results =
    documentIncomeRequirements?.requirements.map((req) =>
      documentIncomeTypeRequirementFileTypeToClassificationResult(req.file_type)
    ) || []

  if (!results.includes(ClassificationResult.UNKNOWN)) {
    results.push(ClassificationResult.UNKNOWN)
  }

  return results
}

export function DocumentsFlowProvider({ children }: Props) {
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentTypeOption | null>(null)
  const [selectedOtherDocumentTypes, setSelectedOtherDocumentTypes] = useState<OtherDocumentType[]>([])
  const { documentIncomeRequirements } = useStore((state) => ({
    documentIncomeRequirements: state.session?.document_income_requirements || null,
  }))
  const hideW2s = shouldHideW2s(documentIncomeRequirements)
  const supportedClassificationTypes = getSupportedDocumentClassificationResults(documentIncomeRequirements)

  function updateSelectedDocumentType(value: DocumentTypeOption | null) {
    setSelectedDocumentType(value)
  }

  function updateSelectedOtherDocumentTypes(value: OtherDocumentType) {
    setSelectedOtherDocumentTypes((prev) => {
      if (prev.includes(value)) {
        return prev.filter((item) => item !== value)
      }

      return [...prev, value]
    })
  }

  function clearOtherDocumentTypeSelections() {
    setSelectedOtherDocumentTypes([])
  }

  return (
    <DocumentsFlowContext.Provider
      value={{
        selectedDocumentType,
        setSelectedDocumentType,
        selectedOtherDocumentTypes,
        setSelectedOtherDocumentTypes,
        updateSelectedDocumentType,
        updateSelectedOtherDocumentTypes,
        clearOtherDocumentTypeSelections,
        documentIncomeRequirements,
        hideW2s,
        supportedClassificationTypes,
      }}
    >
      {children}
    </DocumentsFlowContext.Provider>
  )
}

export function useDocumentsFlowContext() {
  const context = useContext(DocumentsFlowContext)

  if (!context) {
    throw new Error('useDocumentsFlowContext must be used within a DocumentsFlowProvider')
  }

  return context
}
